// Team: Zugänge, Rollen und wer auf welchem Kanal mitarbeitet.
//
// Diese Seite ist der Ort, an dem eine Radioleitung ihre Leute verwaltet.
// Alles hier hängt am Recht `users.manage` – wer es nicht hat, sieht die Seite
// gar nicht erst im Menü und bekommt hier einen Hinweis statt Formulare.
//
// Die Prüfung steht serverseitig. Was hier ausgeblendet wird, ist Bequemlichkeit.

import { useEffect, useMemo, useState } from "react";
import { ROLE_DESCRIPTIONS, ROLE_LABELS, isUserRole } from "@webradiosuite/shared";
import { api, kontoApi } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { usePermission } from "../auth/usePermission";
import { errorText } from "../components/streaming-ui";
import { Initialen, Meldungen, Sym } from "../components/social-ui";
import { Badge, Card, EmptyState, Page, PageHeader, Skeleton } from "../components/ui";
import type { Channel, TeamMember } from "../types";
// h-ui: Freigabe für den Support durch den Betreiber
import { SupportFreigabeKarte } from "../components/support-freigabe";

/** Ein zufälliges, gut abtippbares Startpasswort – ohne verwechselbare Zeichen. */
function startpasswort(): string {
  const zeichen = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const werte = new Uint32Array(16);
  crypto.getRandomValues(werte);
  return Array.from(werte, (v) => zeichen[v % zeichen.length]).join("");
}

export function TeamPage(): JSX.Element {
  const { user } = useAuth();
  const { darf } = usePermission();
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [rollen, setRollen] = useState<Array<{ key: string; label: string }>>([]);
  const [kanaele, setKanaele] = useState<Channel[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [offen, setOffen] = useState<string | null>(null);

  const laden = (): void => {
    api.listTeam().then(setTeam).catch((err) => setFehler(errorText(err)));
  };

  useEffect(() => {
    if (!darf("users.manage")) return;
    laden();
    api.listRoles().then(setRollen).catch(() => undefined);
    api.listChannels().then(setKanaele).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!darf("users.manage")) {
    return (
      <Page className="max-w-5xl">
        <PageHeader eyebrow="Verwalten" title="Team" />
        <div className="card p-2">
          <EmptyState
            icon={<Sym name="shield" className="h-5 w-5" />}
            title="Zugänge und Rollen verwaltet die Radioleitung."
            description={<>Wenn du hier etwas ändern musst, wende dich an sie – oder lass dir die Rolle <b>Radioleitung</b> geben.</>}
          />
        </div>
      </Page>
    );
  }

  const melde = (text: string): void => {
    setHinweis(text);
    setFehler(null);
    window.setTimeout(() => setHinweis(null), 4000);
  };

  const aendern = async (id: string, patch: Parameters<typeof api.updateTeamMember>[1]): Promise<void> => {
    try {
      await api.updateTeamMember(id, patch);
      melde("Gespeichert.");
      laden();
    } catch (err) {
      setFehler(errorText(err));
    }
  };

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Verwalten"
        title="Team"
        description="Wer darf was, und auf welchen Kanälen. Rollen vergibt ausschliesslich die Radioleitung."
        actions={
          <button onClick={() => setNeuOffen((o) => !o)} className={`btn ${neuOffen ? "btn-secondary" : "btn-primary"}`}>
            <Sym name={neuOffen ? "x" : "plus"} />{neuOffen ? "Abbrechen" : "Zugang anlegen"}
          </button>
        }
      />

      <Meldungen error={fehler} notice={hinweis} onClear={() => setHinweis(null)} />

      {neuOffen && (
        <NeuerZugang
          rollen={rollen}
          onFehler={setFehler}
          onFertig={(text) => {
            setNeuOffen(false);
            melde(text);
            laden();
          }}
        />
      )}

      <Card
        padded={false}
        title="Zugänge"
        actions={team && team.length > 0 ? <Badge>{team.length}</Badge> : undefined}
      >
        {team === null && (
          <div className="space-y-2 p-5" aria-label="Wird geladen …">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        )}
        {team?.length === 0 && <p className="px-5 py-4 text-sm text-muted">Noch niemand ausser dir.</p>}
        <div className="divide-y divide-default">
          {team?.map((m) => (
            <Zeile
              key={m.id}
              mitglied={m}
              rollen={rollen}
              kanaele={kanaele}
              selbst={m.id === user?.id}
              offen={offen === m.id}
              onOeffnen={() => setOffen(offen === m.id ? null : m.id)}
              onAendern={aendern}
              onFehler={setFehler}
              onGemeldet={melde}
              onNeuLaden={laden}
            />
          ))}
        </div>
      </Card>

      <RollenSpickzettel rollen={rollen} />

      {/* h-ui: Support durch den Betreiber */}
      {darf("tenant.manage") && <SupportFreigabeKarte />}
    </Page>
  );
}

function RollenSpickzettel({ rollen }: { rollen: Array<{ key: string; label: string }> }): JSX.Element {
  const [offen, setOffen] = useState(false);
  return (
    <div className="card mt-5 overflow-hidden">
      <button onClick={() => setOffen((o) => !o)} className="flex w-full items-center gap-2.5 px-5 py-3.5 text-left text-sm font-medium text-primary hover:bg-surface-hover">
        <Sym name="key" className="h-4 w-4 text-muted" />
        <span className="flex-1">Was die Rollen dürfen</span>
        <Sym name={offen ? "up" : "down"} className="h-4 w-4 text-muted" />
      </button>
      {offen && (
        <ul className="grid gap-px border-t border-default bg-[var(--border)] text-sm sm:grid-cols-2">
          {rollen.map((r) => (
            <li key={r.key} className="bg-card px-5 py-3">
              <b className="font-semibold text-primary">{r.label}</b>
              {isUserRole(r.key) && <p className="mt-0.5 text-xs leading-relaxed text-muted">{ROLE_DESCRIPTIONS[r.key]}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NeuerZugang({
  rollen,
  onFertig,
  onFehler,
}: {
  rollen: Array<{ key: string; label: string }>;
  onFertig: (text: string) => void;
  onFehler: (text: string) => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [rolle, setRolle] = useState("moderator");
  const [passwort, setPasswort] = useState(startpasswort);
  const [laeuft, setLaeuft] = useState(false);

  const anlegen = async (): Promise<void> => {
    setLaeuft(true);
    try {
      await api.createTeamMember({ email, display_name: name, role: rolle, password: passwort });
      onFertig(`Angelegt. Startpasswort: ${passwort} – bitte weitergeben, es steht nirgends sonst.`);
    } catch (err) {
      onFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Card title="Neuer Zugang" className="mb-5 animate-fade-in">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} className="input mt-1.5" placeholder="Anna Beispiel" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          E-Mail
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="input mt-1.5" placeholder="anna@meinradio.de" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Rolle
          <select value={rolle} onChange={(e) => setRolle(e.target.value)} className="input mt-1.5">
            {rollen.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Startpasswort
          <div className="mt-1.5 flex gap-2">
            <input value={passwort} onChange={(e) => setPasswort(e.target.value)} className="input min-w-0 font-mono text-xs" />
            <button onClick={() => setPasswort(startpasswort())} className="btn btn-secondary flex-shrink-0" title="Neues erzeugen">neu</button>
          </div>
        </label>
      </div>
      <p className="field-hint mt-3">
        Das Startpasswort wird nach dem Anlegen einmal angezeigt und ist danach nicht mehr auslesbar.
        Die Person ändert es unter „Mein Zugang“ selbst.
      </p>
      <button
        onClick={anlegen}
        disabled={laeuft || !email || !name || passwort.length < 12}
        className="btn btn-primary mt-4"
      >
        Anlegen
      </button>
    </Card>
  );
}

function Zeile({
  mitglied,
  rollen,
  kanaele,
  selbst,
  offen,
  onOeffnen,
  onAendern,
  onFehler,
  onGemeldet,
  onNeuLaden,
}: {
  mitglied: TeamMember;
  rollen: Array<{ key: string; label: string }>;
  kanaele: Channel[];
  selbst: boolean;
  offen: boolean;
  onOeffnen: () => void;
  onAendern: (id: string, patch: { display_name?: string; role?: string; editorial_lead?: boolean }) => Promise<void>;
  onFehler: (t: string) => void;
  onGemeldet: (t: string) => void;
  onNeuLaden: () => void;
}): JSX.Element {
  const mitglieds = useMemo(() => new Map(mitglied.channels.map((c) => [c.id, c.role])), [mitglied.channels]);
  // Der Name ist nicht nur Zierde: Er steht im Hörerchat an jeder Zeile, im
  // Posteingang an der Zuteilung und im Studio über dem Sendepult.
  const [name, setName] = useState(mitglied.display_name ?? "");

  const nameSichern = async (): Promise<void> => {
    const neu = name.trim();
    if (neu === (mitglied.display_name ?? "") || neu === "") return;
    await onAendern(mitglied.id, { display_name: neu });
  };

  const kanalSetzen = async (channelId: string, rolle: string): Promise<void> => {
    try {
      if (rolle === "") await api.removeChannelMember(channelId, mitglied.id);
      else await api.setChannelMember(channelId, mitglied.id, rolle);
      onGemeldet("Kanalzugang gespeichert.");
      onNeuLaden();
    } catch (err) {
      onFehler(errorText(err));
    }
  };

  const passwortSetzen = async (): Promise<void> => {
    const neu = startpasswort();
    try {
      await api.resetTeamPassword(mitglied.id, neu);
      onGemeldet(`Neues Passwort für ${mitglied.display_name ?? mitglied.email}: ${neu} – alle Sitzungen wurden beendet.`);
    } catch (err) {
      onFehler(errorText(err));
    }
  };

  // f-auth: Handy verloren, keine Wiederherstellungscodes mehr – die Radioleitung schaltet ab.
  const zweiFaktorZuruecksetzen = async (): Promise<void> => {
    const wer = mitglied.display_name ?? mitglied.email;
    if (!window.confirm(`Zwei-Faktor-Anmeldung von ${wer} abschalten? Danach reicht für diesen Zugang wieder das Passwort, bis ${wer} sie neu einrichtet.`)) return;
    try {
      await kontoApi.teamZweiFaktorZuruecksetzen(mitglied.id);
      onGemeldet(`Zwei-Faktor-Anmeldung von ${wer} abgeschaltet. Sie kann unter „Mein Konto“ neu eingerichtet werden.`);
      onNeuLaden();
    } catch (err) {
      onFehler(errorText(err));
    }
  };

  const entfernen = async (): Promise<void> => {
    if (!window.confirm(`Zugang von ${mitglied.display_name ?? mitglied.email} wirklich entfernen?`)) return;
    try {
      await api.deleteTeamMember(mitglied.id);
      onGemeldet("Zugang entfernt.");
      onNeuLaden();
    } catch (err) {
      onFehler(errorText(err));
    }
  };

  return (
    <div className={offen ? "bg-surface-sunken/50" : ""}>
      <button onClick={onOeffnen} className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-left hover:bg-surface-hover">
        <Initialen name={mitglied.display_name ?? mitglied.email} />
        <span className="min-w-0 flex-1 basis-40">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-primary">{mitglied.display_name ?? mitglied.email}</span>
            {selbst && <span className="text-xs text-muted">(du)</span>}
          </span>
          {mitglied.display_name && <span className="block truncate text-xs text-muted">{mitglied.email}</span>}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone={mitglied.role === "tenant_admin" || mitglied.role === "super_admin" ? "brand" : "neutral"}>{mitglied.role_label}</Badge>
          {mitglied.editorial_lead && <Badge tone="warning">Redaktionsleitung</Badge>}
          {/* f-auth: Zwei-Faktor auf einen Blick */}
          {mitglied.two_factor && <span title="Zwei-Faktor-Anmeldung an"><Badge tone="success"><Sym name="shield" className="h-3 w-3" />2FA</Badge></span>}
        </span>
        <span className="w-24 text-right text-xs text-muted">
          {mitglied.channels.length === 0 ? "kein Kanal" : `${mitglied.channels.length} Kanäle`}
        </span>
        <Sym name={offen ? "up" : "down"} className="h-4 w-4 text-muted" />
      </button>

      {offen && (
        <div className="space-y-5 px-5 pb-5 pt-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-[0.8rem] font-medium text-secondary">
              Name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => void nameSichern()}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void nameSichern(); } }}
                maxLength={120}
                placeholder={mitglied.email}
                className="input mt-1.5"
              />
              <span className="field-hint block font-normal">
                So steht es im Hörerchat, im Posteingang und im Studio. Leer: die E-Mail-Adresse.
              </span>
            </label>
            <label className="block text-[0.8rem] font-medium text-secondary">
              Rolle im Radio
              <select
                value={mitglied.role}
                disabled={selbst}
                onChange={(e) => onAendern(mitglied.id, { role: e.target.value })}
                className="input mt-1.5 disabled:opacity-60"
              >
                {rollen.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              {selbst && (
                <span className="field-hint block font-normal">
                  Die eigene Rolle lässt sich nicht ändern – sonst könnte man sich selbst hochstufen.
                </span>
              )}
            </label>
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-default bg-card p-3 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={mitglied.editorial_lead}
                onChange={(e) => onAendern(mitglied.id, { editorial_lead: e.target.checked })}
                className="mt-0.5 h-4 w-4"
              />
              <span className="text-primary">
                Redaktionsleitung
                <span className="block text-xs text-muted">darf Beiträge freigeben, planen und zurückziehen</span>
              </span>
            </label>
          </div>

          <div>
            <div className="text-[0.8rem] font-medium text-secondary">Kanäle</div>
            <p className="text-xs text-muted">
              Ohne Eintrag sieht diese Person den Kanal nicht. Die Kanalrolle kann die Rolle im Radio
              anheben – wegnehmen kann sie nichts.
            </p>
            <div className="mt-2 divide-y divide-default overflow-hidden rounded-lg border border-default bg-card">
              {kanaele.length === 0 && <p className="px-3 py-2 text-xs text-muted">Noch keine Kanäle angelegt.</p>}
              {kanaele.map((k) => (
                <div key={k.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <Sym name="radio" className="h-4 w-4 text-muted" />
                  <span className="min-w-[10rem] flex-1 text-primary">{k.name}</span>
                  <select
                    value={mitglieds.get(k.id) ?? ""}
                    onChange={(e) => kanalSetzen(k.id, e.target.value)}
                    className="input h-8 w-auto min-w-[12rem] text-sm"
                  >
                    <option value="">— kein Zugang —</option>
                    {rollen.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-default pt-4">
            <button onClick={passwortSetzen} className="btn btn-secondary btn-sm">
              <Sym name="key" className="h-3.5 w-3.5" />Passwort zurücksetzen
            </button>
            {mitglied.two_factor ? (
              <button onClick={zweiFaktorZuruecksetzen} className="btn btn-secondary btn-sm" title="Zwei-Faktor-Anmeldung ist an">
                <Sym name="shield" className="h-3.5 w-3.5" />2FA zurücksetzen
              </button>
            ) : (
              <span className="flex items-center gap-1.5 px-1 text-xs text-muted" title="Einschalten kann jede Person selbst unter „Mein Konto“">
                <Sym name="shield" className="h-3.5 w-3.5" />2FA aus
              </span>
            )}
            {!selbst && (
              <button onClick={entfernen} className="btn btn-ghost btn-sm !text-red-600 dark:!text-red-400">
                <Sym name="trash" className="h-3.5 w-3.5" />Zugang entfernen
              </button>
            )}
            <span className="ml-auto flex items-center gap-1.5 text-xs text-muted">
              <Sym name="clock" className="h-3.5 w-3.5" />
              {mitglied.last_login_at
                ? `zuletzt angemeldet: ${new Date(mitglied.last_login_at).toLocaleString("de-DE")}`
                : "noch nie angemeldet"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export { ROLE_LABELS };
